import React from 'react';
import { Plus, Trash2, Edit3 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { createBusinessOptions } from '../../data/optionsData';

const BusinessTypeButtons = ({
  loadingBusinesses,
  userBusinesses,
  user,
  businessType,
  setBusinessType,
  setMessages,
  handleCloseBusinessOptionsModal,
  handleRemoveSecondBusiness,
  handleAddPrimaryBusiness,
  handleRemovePrimaryBusiness,
  theme,
  setShowBusinessOptionsModal,
  setShowSecondBusinessModal,
  setShowPrimaryBusinessModal,
  refreshUserData,
  scrollToBottom
}) => {
  const { t } = useLanguage();
  const businessOptions = React.useMemo(() => createBusinessOptions(t), [t]);

  const getBusinessLabel = (value) => {
    if (!value) return '';

    const normalized = value.trim().toLowerCase();
    const matchedOption = businessOptions.find(
      (option) => option.type.toLowerCase() === normalized
    );
    if (matchedOption?.label) {
      return matchedOption.label;
    }

    const camelKey = value
      .trim()
      .split(/[\s/_-]+/)
      .filter(Boolean)
      .map((word, index) => {
        const lower = word.toLowerCase();
        return index === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join('');

    if (camelKey) {
      const translation = t(`business.${camelKey}`);
      if (translation && translation !== `business.${camelKey}`) {
        return translation;
      }
    }

    return value
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  if (loadingBusinesses) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        fontSize: '12px',
        color: theme.tertiaryText
      }}>
        {t('fullchat.loadingBusinesses')}
      </div>
    );
  }

  // Check if user has a primary business type
  const userHasPrimaryBusiness = user?.business_type && user.business_type.trim() !== '';
  
  console.log('🔍 BusinessTypeButtons Debug:', {
    userHasPrimaryBusiness,
    userBusinessType: user?.business_type,
    userBusinessesLength: userBusinesses.length,
    userSecondBusiness: user?.second_business_type,
    shouldShowAddSecondBusiness: userHasPrimaryBusiness && !user?.second_business_type,
    shouldShowAddPrimaryBusiness: !userHasPrimaryBusiness,
    componentWillRender: !!user,
    fullUser: user
  });

  // Always show the component for authenticated users
  if (user) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        alignItems: 'flex-end'
      }}>

        {/* Primary Business Display or Add Primary Business Button */}
        {userHasPrimaryBusiness ? (
          // Show primary business with orange background, black font, and remove option
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <button
              onClick={() => {
                setBusinessType(user.business_type);
                const businessLabel = getBusinessLabel(user.business_type);
                const userMsg = t('fullchat.switchingToBusiness').replace(/\{\{business\}\}/g, businessLabel);
                const userMessage = { type: 'user', content: userMsg, timestamp: new Date() };
                setMessages(prev => [...prev, userMessage]);
                const botMsg = t('fullchat.nowTalkingAboutBusiness').replace(/\{\{business\}\}/g, businessLabel);
                const botMessage = { type: 'question', content: botMsg, timestamp: new Date() };
                setMessages(prev => [...prev, botMessage]);
                
                // Auto-scroll to bottom after business switch
                if (scrollToBottom) {
                  setTimeout(() => {
                    scrollToBottom();
                  }, 200);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: businessType === user.business_type 
                  ? 'linear-gradient(135deg, #fbbf24 0%, #f59e42 100%)' 
                  : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                color: businessType === user.business_type ? '#000' : '#fff',
                border: 'none',
                borderRadius: '16px',
                padding: '12px 16px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: businessType === user.business_type 
                  ? '0 2px 8px rgba(251, 191, 36, 0.3)' 
                  : '0 2px 8px rgba(107, 114, 128, 0.3)',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = businessType === user.business_type 
                  ? '0 4px 16px rgba(251, 191, 36, 0.4)' 
                  : '0 4px 16px rgba(107, 114, 128, 0.4)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = businessType === user.business_type 
                  ? '0 2px 8px rgba(251, 191, 36, 0.3)' 
                  : '0 2px 8px rgba(107, 114, 128, 0.3)';
              }}
              title={t('fullchat.switchToBusinessTitle').replace(/\{\{business\}\}/g, getBusinessLabel(user.business_type))}
            >
              <span style={{ marginRight: '8px' }}>
                {businessType === user.business_type ? '✓' : '○'}
              </span>
              {getBusinessLabel(user.business_type)}
            </button>
            
            {/* Change button for primary business */}
            <button
              onClick={() => {
                console.log('🚀 Change Primary Business button clicked');
                setShowPrimaryBusinessModal(true);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                padding: '8px',
                fontSize: '10px',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                transition: 'all 0.2s ease',
                minWidth: '24px',
                height: '24px',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
              }}
              title={t('fullchat.switchToBusinessTitle').replace(/\{\{business\}\}/g, getBusinessLabel(user.business_type))}
            >
              <Edit3 size={12} />
            </button>
          </div>
        ) : (
          // Show Add Primary Business button when user has no primary business
          <button
            onClick={() => {
              console.log('🚀 Add Primary Business button clicked');
              setShowPrimaryBusinessModal(true);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '16px',
              padding: '12px 16px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
            }}
            title={t('fullchat.addPrimaryBusinessType')}
          >
            <Plus size={14} />
            {t('fullchat.addPrimaryBusiness')}
          </button>
        )}

        {/* Second Business Type or Add Business Button - Only show if user has primary business */}
        {userHasPrimaryBusiness && user?.second_business_type ? (
          // Show second business with remove option
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <button
              onClick={() => {
                setBusinessType(user.second_business_type);
                const businessLabel = getBusinessLabel(user.second_business_type);
                const userMsg = t('fullchat.switchingToBusiness').replace(/\{\{business\}\}/g, businessLabel);
                const userMessage = { type: 'user', content: userMsg, timestamp: new Date() };
                setMessages(prev => [...prev, userMessage]);
                const botMsg = t('fullchat.nowTalkingAboutBusiness').replace(/\{\{business\}\}/g, businessLabel);
                const botMessage = { type: 'question', content: botMsg, timestamp: new Date() };
                setMessages(prev => [...prev, botMessage]);
                
                // Auto-scroll to bottom after business switch
                if (scrollToBottom) {
                  setTimeout(() => {
                    scrollToBottom();
                  }, 200);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: businessType === user.second_business_type 
                  ? 'linear-gradient(135deg, #fbbf24 0%, #f59e42 100%)' 
                  : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                color: businessType === user.second_business_type ? '#000' : '#fff',
                border: 'none',
                borderRadius: '16px',
                padding: '12px 16px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: businessType === user.second_business_type 
                  ? '0 2px 8px rgba(251, 191, 36, 0.3)' 
                  : '0 2px 8px rgba(107, 114, 128, 0.3)',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = businessType === user.second_business_type 
                  ? '0 4px 16px rgba(251, 191, 36, 0.4)' 
                  : '0 4px 16px rgba(107, 114, 128, 0.4)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = businessType === user.second_business_type 
                  ? '0 2px 8px rgba(251, 191, 36, 0.3)' 
                  : '0 2px 8px rgba(107, 114, 128, 0.3)';
              }}
              title={t('fullchat.switchToBusinessTitle').replace(/\{\{business\}\}/g, getBusinessLabel(user.second_business_type))}
            >
              <span style={{ marginRight: '8px' }}>
                {businessType === user.second_business_type ? '✓' : '○'}
              </span>
              {getBusinessLabel(user.second_business_type)}
            </button>
            
            {/* Remove button for second business */}
            <button
                          onClick={() => {
              if (window.confirm(t('fullchat.removeSecondBusinessConfirm').replace(/\{\{business\}\}/g, getBusinessLabel(user.second_business_type)))) {
                handleRemoveSecondBusiness();
                // Refresh user data after removal
                if (refreshUserData) {
                  setTimeout(() => refreshUserData(), 500);
                }
              }
            }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                padding: '8px',
                fontSize: '10px',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                transition: 'all 0.2s ease',
                minWidth: '24px',
                height: '24px',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(239, 68, 68, 0.4)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.3)';
              }}
              title={`Remove ${user.second_business_type?.split(' ').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
              ).join(' ')} as second business`}
            >
              <Trash2 size={12} />
            </button>
          </div>
        ) : userHasPrimaryBusiness ? (
          // Show Add Second Business button - only if user has primary business
          <button
            onClick={() => {
              console.log('🚀 Add Second Business button clicked');
              setShowSecondBusinessModal(true);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '16px',
              padding: '12px 16px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3)',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(107, 114, 128, 0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(107, 114, 128, 0.3)';
            }}
            title="Add a second business type"
          >
            <Plus size={14} />
            {t('fullchat.addSecondBusiness')}
          </button>
        ) : null}
      </div>
    );
  }

  // No businesses exist - return null
  return null;
};

export default BusinessTypeButtons;
